/**
 * `bootstrapAuth` — wire the shared axios client to the Redux store and
 * reconcile any "Redux says authenticated, tokenStore says no token"
 * inconsistency that arises from HMR, a dev-server restart, or a hard
 * browser reload.
 *
 * Tokens are in-memory only (spec §5.3.8). The user profile may persist
 * to localStorage for optimistic UX. On a hard reload the profile survives
 * but the in-memory tokens are gone — this app's HttpOnly
 * `dfq_refresh_token_<client>` cookie (server-set) is the recovery path. The
 * console and storefront each have their own cookie, so the booting app
 * names itself and only ever recovers its own session.
 *
 * Boot sequence when a cached profile exists but no access token is present:
 *
 *   1. Dispatch `auth/bootPhasePending` — gates `RequireAuth` so it does not
 *      redirect to /login before the cookie refresh attempt resolves.
 *   2. Attempt a silent `/auth/refresh` POST naming this app's client (no
 *      Bearer; the HttpOnly cookie is sent automatically via
 *      `withCredentials: true` on the axios client).
 *   3a. Success → `setTokens` updates the in-memory store, `/auth/me`
 *       replaces the cached profile (the cookie may belong to a different
 *       account), then dispatch `auth/bootPhaseReady`.
 *   3b. Failure → dispatch `auth/tokensExpired` (clears slice) + dispatch
 *       `auth/bootPhaseReady`. `RequireAuth` will then redirect to /login.
 *
 * SF-AUTH-PERSIST.
 */
import { me } from '@dfq/shared/api/auth';
import {
  configureApiClient,
  performRefresh,
  registerAuthStore,
  type StoreLike,
} from '@dfq/shared/api/client';
import { getAccessToken } from '@dfq/shared/auth/tokenStore';
import type { SpaAuthClient } from '@dfq/shared/types/auth';

const SESSION_RESTORE_PARAM = 'session_restore';

interface AuthStateShape {
  user: unknown;
  isAuthenticated: boolean;
}

interface BootstrapStore extends StoreLike {
  getState: () => { auth: AuthStateShape };
}

export interface BootstrapAuthOptions {
  /** The first-party SPA that is booting. Sent on refresh and logout. */
  clientType: SpaAuthClient;
}


/**
 * True when the URL carries the login-link marker, which is stripped as it is
 * read so a reload does not retry a refresh that already failed.
 */
function consumeSessionRestoreMarker(): boolean {
  if (typeof window === 'undefined' || typeof window.location === 'undefined') {
    return false;
  }

  try {
    const url = new URL(window.location.href);

    if (url.searchParams.get(SESSION_RESTORE_PARAM) === null) {
      return false;
    }

    url.searchParams.delete(SESSION_RESTORE_PARAM);
    window.history.replaceState({}, '', url.toString());

    return true;
  } catch {
    return false;
  }
}

export async function bootstrapAuth(
  store: BootstrapStore,
  options: BootstrapAuthOptions,
): Promise<void> {
  configureApiClient({ clientType: options.clientType });
  registerAuthStore(store);

  if (getAccessToken() !== null) {
    // Tokens already live in memory (HMR hot-swap, same-tab re-render).
    // Nothing to recover — leave the store untouched.
    return;
  }

  // Restore the cached user profile from localStorage. The profile is the only
  // persisted auth artifact (tokens are in-memory only), so without this the
  // session-shape check below is always false on a hard reload and the cookie
  // refresh never fires.
  store.dispatch({ type: 'auth/hydrateFromStorage' });

  const auth = store.getState().auth;
  // A server-established session (PL-INFRA-020 login link) arrives with the
  // refresh cookie but no cached profile, so the shape check below would skip
  // the very refresh that would recover it. The marker says "a cookie was just
  // set for you" — it grants nothing on its own; without the cookie the
  // refresh simply fails and the guard still redirects.
  const hasSessionShape =
    auth.isAuthenticated || auth.user !== null || consumeSessionRestoreMarker();

  if (!hasSessionShape) {
    // Fully signed out (no cached profile) — nothing to recover.
    return;
  }

  // Profile is hydrated from localStorage but tokens are gone (hard reload).
  // Gate route guards while the cookie refresh attempt is in flight.
  store.dispatch({ type: 'auth/bootPhasePending' });

  try {
    // performRefresh side-effects the rotated tokens into the in-memory store
    // and returns the authorization payload. Mark the slice authenticated
    // (heartbeatSuccess sets isAuthenticated + the expiry mirror) — hydrate
    // already restored the user block, so RequireAuth now passes.
    const authorization = await performRefresh();
    store.dispatch({ type: 'auth/heartbeatSuccess', payload: authorization });

    // The refresh cookie can belong to a different account than the cached
    // profile (login link, sign-in from another tab), so always re-read the user.
    const hadCachedProfile = store.getState().auth.user !== null;
    try {
      const user = await me();
      store.dispatch({ type: 'auth/profileLoaded', payload: user });
    } catch (error) {
      // Refresh succeeded, so the session is live: keep the cached profile
      // rather than signing out. Without a cached profile route guards have no
      // role to read, so that case still fails the boot.
      if (!hadCachedProfile) throw error;
    }
  } catch {
    store.dispatch({ type: 'auth/tokensExpired' });
  } finally {
    store.dispatch({ type: 'auth/bootPhaseReady' });
  }
}
